import type { InputHTMLAttributes, Ref } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon/Icon';
import type { FieldContext } from '../SearchField/SearchField';

/**
 * A single-line text control. `text-form md:text-sm` is deliberate: 16px on a
 * phone stops iOS from zooming the viewport on focus, and desktop gets the
 * dense size back.
 *
 * IT DECLARES ITS CONTEXT, LIKE `SearchField` (wave 4 round 1, C2-18). The kit draws a text
 * field in three places and this primitive is the content area's:
 * `Text Fields/{Light,Dark}/5 XL/Value` is a `#FFFFFF` / `#1E1E1E` box with a 1px OUTSIDE
 * hairline at 5 percent / 4 percent and corners at height/4, not the bordered rectangle on
 * a raised surface this component drew. A caller in chrome states which of the other two
 * shells it wants; nobody has to, and nobody can leave the question unanswered.
 */
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  readonly invalid?: boolean | undefined;
  readonly icon?: IconName | undefined;
  /** Renders flush inside a table cell: no border, no radius, no padding jump. */
  readonly bare?: boolean | undefined;
  /** Which of the kit's three field shells this is; the content area's by default. */
  readonly context?: FieldContext | undefined;
  /** The underlying field, for a list that moves focus into its own search box (D10). */
  readonly ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * THE FLOOR STAYS OURS (E5, "our floor wins"). The kit's content field is 36px and this
 * product presses at 40 on a pointer and 44 on a finger, the same disagreement the toolbar
 * search settled the same way: the SHELL is the kit's - fill, hairline and radius - and the
 * box keeps the floor, because an input cannot grow a pseudo-element to reach one.
 */
export const INPUT_BASE =
  'field-text w-full min-h-(--touch-target-min) px-3 py-2 text-form md:text-sm ' +
  'text-ink transition-colors ' +
  'disabled:bg-surface-sunken disabled:opacity-70';

const BARE =
  'w-full min-h-(--touch-target-min) rounded-none border-0 bg-transparent px-2 py-1 text-form md:text-sm text-ink ' +
  'placeholder:text-ink-subtle';

export function Input({
  invalid = false,
  icon,
  bare = false,
  context = 'content',
  className,
  ...rest
}: InputProps) {
  /**
   * A FIELD WITH A MAGNIFIER IN IT IS A SEARCH FIELD, AND THE KIT DRAWS THOSE AS CAPSULES
   * (wave 4 round 1, C2-18). `Search Fields/{Light,Dark}/{1 Mn..5 XL}/*` carries
   * `radii [3.4e38]` at EVERY size, where `Text Fields/.../5 XL/Value` carries `[9]` - the
   * same fill and the same outside hairline, a different shape. Three callers already say
   * which one they are by handing in the search glyph (`ui/OptionList`, `ui/PickerList`,
   * `app/workflows/RunNowDialog`), so the shape follows the glyph rather than a fourth prop
   * nobody would remember to pass; `type="search"` says it too, for a field with no glyph.
   */
  const searching = icon === 'search' || rest.type === 'search';
  const control = (
    <input
      aria-invalid={invalid || undefined}
      data-field-context={context}
      className={cx(
        bare ? BARE : INPUT_BASE,
        !bare && `field-text-${context}`,
        !bare && searching && 'field-text-searching',
        !bare && invalid && 'field-invalid',
        icon !== undefined && !bare && 'pl-9',
        className,
      )}
      {...rest}
    />
  );

  if (icon === undefined) {
    return control;
  }
  return (
    <span className="relative block">
      <Icon
        name={icon}
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-subtle"
      />
      {control}
    </span>
  );
}
